/**
 * useErrorLogger — global hook that captures uncaught runtime errors and
 * unhandled promise rejections, POSTing them to the backend so we get a
 * "did the deploy work" canary within minutes (instead of after a user
 * screen-recording).
 *
 * Was inspired by the Feb 2026 react-helmet-async@3.0.0 / React 19 crash
 * that silently broke SPA navigation in production. With this hook the
 * crash would have surfaced server-side in seconds.
 *
 * Lightweight on purpose: no Sentry dep, no source-map upload, no PII.
 * Just message + stack + url + route + user_id. Per-session rate-limit
 * (max 5 errors per 60s) so a runaway exception loop doesn't flood the
 * backend.
 *
 * Mount once at the App root.
 */
import { useEffect } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;
const WINDOW_MS = 60 * 1000;
const MAX_PER_WINDOW = 5;

// Module-scoped — survives StrictMode double-invocation.
const sentRecently = [];

function shouldSend() {
  const now = Date.now();
  // Drop records older than the rate-limit window.
  while (sentRecently.length && now - sentRecently[0] > WINDOW_MS) {
    sentRecently.shift();
  }
  if (sentRecently.length >= MAX_PER_WINDOW) return false;
  sentRecently.push(now);
  return true;
}

function getCurrentUserId() {
  // Best-effort — the AuthContext writes the JWT to localStorage but the
  // user_id isn't easily available outside React. We send null and let
  // the backend leave it blank; if you want user attribution later, wire
  // it through React state.
  return null;
}

async function report({ message, stack, kind }) {
  if (!API || !shouldSend()) return;
  // Skip benign noise from browser extensions or third-party scripts
  // that throw inside their own sandboxes.
  if (typeof message === 'string' && (
    message.includes('ResizeObserver loop')
    || message.includes('Non-Error promise rejection')
    || message.includes('Script error.')
  )) return;

  try {
    await fetch(`${API}/api/errors/log`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      keepalive: true, // survive page unload
      body: JSON.stringify({
        message: String(message || 'unknown').slice(0, 500),
        stack: stack ? String(stack).slice(0, 2000) : null,
        url: window.location.href.slice(0, 500),
        route: window.location.pathname.slice(0, 200),
        user_agent: navigator.userAgent.slice(0, 400),
        kind: kind || 'error',
        user_id: getCurrentUserId(),
      }),
    });
  } catch {
    // Swallow — error logger MUST NEVER throw.
  }
}

export function useErrorLogger() {
  useEffect(() => {
    const onError = (event) => {
      report({
        message: event.message || (event.error && event.error.message) || 'window.error',
        stack: event.error?.stack,
        kind: 'error',
      });
    };
    const onRejection = (event) => {
      const reason = event.reason;
      report({
        message: (reason && (reason.message || String(reason))) || 'unhandledrejection',
        stack: reason && reason.stack,
        kind: 'unhandledrejection',
      });
    };
    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onRejection);
    return () => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onRejection);
    };
  }, []);
}

export default useErrorLogger;
